import React from 'react';
import { motion, useMotionTemplate, useMotionValue } from 'framer-motion';
import { ArrowRightIcon } from 'lucide-react';
import { HeroMockup } from './HeroMockup';
import { FloatingBadges } from './FloatingBadges';
import { premiumEasing } from '../utils/animations';
export function Hero() {
  const mouseX = useMotionValue(0);
  const mouseY = useMotionValue(0);
  function handleMouseMove({
    currentTarget,
    clientX,
    clientY
  }: MouseEvent) {
    const {
      left,
      top
    } = currentTarget.getBoundingClientRect();
    mouseX.set(clientX - left);
    mouseY.set(clientY - top);
  }
  return <section className="relative min-h-screen flex items-center pt-28 sm:pt-24 pb-16 sm:pb-12 overflow-hidden group" onMouseMove={handleMouseMove} data-id="element-144">
      <motion.div className="pointer-events-none absolute -inset-px opacity-0 transition duration-300 group-hover:opacity-100 z-0" style={{
      background: useMotionTemplate`
            radial-gradient(
              600px circle at ${mouseX}px ${mouseY}px,
              rgba(168, 85, 247, 0.12),
              transparent 80%
            )
          `
    }} data-id="element-145" />

      <div className="max-w-7xl mx-auto px-6 md:px-12 w-full relative z-10" data-id="element-146">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center" data-id="element-147">
          <motion.div initial={{
          opacity: 0,
          y: 40
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8,
          ease: premiumEasing
        }} className="flex flex-col items-start" data-id="element-148">
            <div className="inline-flex items-center space-x-2 px-3 py-1 rounded-full bg-white/5 border border-white/10 mb-8" data-id="element-149">
              <span className="relative flex h-2 w-2" data-id="element-150">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-electric opacity-75" data-id="element-151"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-electric" data-id="element-152"></span>
              </span>
              <span className="text-xs font-medium tracking-wider text-neutral-300 uppercase" data-id="element-153">
                AI Web Studio · LATAM
              </span>
            </div>

            <h1 className="text-4xl sm:text-5xl md:text-7xl lg:text-[5rem] font-bold leading-[1.08] tracking-tighter text-white mb-6 md:mb-8" data-id="element-154">
              Mientras tu competencia{' '}
              <span className="text-gradient animate-gradient-shift bg-gradient-to-r from-electric via-neon to-cyan drop-shadow-[0_0_20px_rgba(168,85,247,0.5)]" data-id="element-155">
                vende
              </span>{' '}
              online, tu negocio sigue{' '}
              <span className="text-gradient animate-gradient-shift bg-gradient-to-r from-cyan via-neon to-electric drop-shadow-[0_0_20px_rgba(34,211,238,0.5)]" data-id="element-156">
                invisible.
              </span>
            </h1>

            <p className="text-base sm:text-lg md:text-xl text-neutral-400 leading-relaxed max-w-xl mb-6 font-light" data-id="element-157">
              Creamos webs premium en 72hs para clínicas, restaurantes, ópticas
              y hoteles que necesitan verse modernos, generar confianza y
              convertir visitas en clientes.
            </p>

            <div className="flex flex-wrap items-center gap-2 sm:gap-3 text-xs sm:text-sm font-medium text-neutral-300 mb-10" data-id="element-158">
              <span className="flex items-center" data-id="element-159">
                <span className="w-1.5 h-1.5 rounded-full bg-electric mr-2" data-id="element-160"></span>
                Desde USD 390
              </span>
              <span className="hidden sm:inline text-neutral-600" data-id="element-161">•</span>
              <span className="flex items-center" data-id="element-162">
                <span className="w-1.5 h-1.5 rounded-full bg-neon mr-2 sm:hidden" data-id="element-163"></span>
                Dominio incluido
              </span>
              <span className="hidden sm:inline text-neutral-600" data-id="element-164">•</span>
              <span className="flex items-center" data-id="element-165">
                <span className="w-1.5 h-1.5 rounded-full bg-cyan mr-2 sm:hidden" data-id="element-166"></span>
                Entrega en 72hs
              </span>
              <span className="hidden sm:inline text-neutral-600" data-id="element-167">•</span>
              <span className="flex items-center" data-id="element-168">
                <span className="w-1.5 h-1.5 rounded-full bg-green-400 mr-2 sm:hidden" data-id="element-169"></span>
                1 año de mantenimiento
              </span>
            </div>

            <div className="flex flex-col sm:flex-row items-center space-y-4 sm:space-y-0 sm:space-x-6 w-full sm:w-auto" data-id="element-170">
              <a href="https://wa.me/59898838657?text=%C2%A1Hola%21%20Quiero%20activar%20mi%20web%20premium%20con%20WebsiteIA.%20%C2%BFMe%20asesoran%3F" target="_blank" rel="noreferrer" className="relative w-full sm:w-auto inline-flex items-center justify-center px-8 py-4 text-base font-semibold text-white transition-all duration-300 bg-white/5 border border-white/10 rounded-full hover:bg-white/10 hover:border-white/20 hover:scale-105 group overflow-hidden" data-id="element-171">
                <span className="relative z-10 flex items-center" data-id="element-172">
                  Obtener mi web en 72hs
                  <ArrowRightIcon className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" data-id="element-173" />
                </span>
                <div className="absolute inset-0 bg-gradient-to-r from-electric/20 to-neon/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300" data-id="element-174"></div>
                <div className="absolute -inset-1 bg-gradient-to-r from-electric to-cyan opacity-0 group-hover:opacity-30 blur-xl transition-opacity duration-300" data-id="element-175"></div>
              </a>

              <a href="#industrias" className="w-full sm:w-auto inline-flex items-center justify-center px-8 py-4 text-base font-medium text-neutral-300 transition-colors hover:text-white" data-id="element-176">
                Ver ejemplos
              </a>
            </div>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          scale: 0.95
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 1,
          ease: premiumEasing,
          delay: 0.2
        }} className="relative" data-id="element-177">
            <FloatingBadges data-id="element-178" />
            <HeroMockup data-id="element-179" />
          </motion.div>
        </div>
      </div>
    </section>;
}